import React from "react";

/**
 * Props for the GenericDropdown component.
 */
export interface GenericCheckboxProps {
  /**
   * Label to display in the checkbox.
   */
  label?: string; // changed from String
  /**
   * State to control the checkbox.
   */
  checked: boolean; // changed from Boolean
  /**
   * Callback when the checkbox is toggled.
   */
  onChange: (checked: boolean) => void;
  /**
   * Optional to disable the checkbox
   */
  disabled?: boolean; // changed from Boolean
  /**
   * Theme prop for customization
   */
  theme?: "light" | "dark";
  /**
   * Optional size: small, medium or large
   */
  size?: "sm" | "md" | "lg";
  /**
   * Additional custom styles
   */
  className?: string; // changed from String
  subtitle?: string;
}

/**
 * A Check icon as a separate component.
 */
const CheckIcon = ({ className = "" }: { className?: string }) => (
  <svg
    width="12"
    height="7"
    viewBox="0 0 15 10"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={`h-2.5 w-2.5 ${className} fill-current`}
  >
    <path
      d="M13.17 0.850119L6.49 7.53012L2.61 3.65012C2.42264 3.46387 2.16919 3.35933 1.905 3.35933C1.64082 3.35933 1.38737 3.46387 1.2 3.65012C1.01375 3.83748 0.90921 4.09093 0.90921 4.35512C0.90921 4.6193 1.01375 4.87276 1.2 5.06012L5.79 9.65012C5.97737 9.83637 6.23082 9.94091 6.495 9.94091C6.75919 9.94091 7.01264 9.83637 7.2 9.65012L14.58 2.27012C14.6737 2.17716 14.7481 2.06655 14.7989 1.94469C14.8497 1.82284 14.8758 1.69213 14.8758 1.56012C14.8758 1.42811 14.8497 1.2974 14.7989 1.17554C14.7481 1.05368 14.6737 0.943082 14.58 0.850119C14.3926 0.663868 14.1392 0.559326 13.875 0.559326C13.6108 0.559326 13.3574 0.663868 13.17 0.850119Z"
      fill="currentColor"
    />
  </svg>
);

export const GenericCheckbox: React.FC<GenericCheckboxProps> = ({
  label,
  checked,
  onChange,
  disabled = false,
  size = "md",
  className = "",
  subtitle,
}) => {
  const handleToggle = () => {
    if (!disabled) {
      onChange(!checked);
    }
  };

  const sizeClasses = {
    sm: "w-4 h-4",
    md: "w-5 h-5",
    lg: "w-6 h-6",
  };

  return (
    <div
      className={`flex items-center space-x-2 cursor-pointer ${disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"
        } ${className}`}
      onClick={handleToggle}
    >
      <div
        className={`flex justify-center items-center rounded border transition-all duration-200 ${sizeClasses[size]
          } ${checked ? "bg-semantic-bg-brand-bold border-none" : "border-semantic-bg-brand-bold bg-transparent"}`}
      >
        {checked && <CheckIcon className={"text-semantic-border-secondary"} />}
      </div>
      <div className="flex flex-col justify-center min-w-[130px] max-w-[150px] text-sm text-semantic-text-primary text-center whitespace-normal leading-tight">
        {label && <span className="font-medium">{label}</span>}
        {subtitle && <span className="text-xs text-gray-500">{subtitle}</span>}
      </div>
    </div>
  );
};

export default GenericCheckbox;
